<template>
  <v-sheet :class="`bg-${color}`" class="py-16">
    <v-container>
      <v-row>
        <v-col align-self="center" class="pr-xl-16" order="2" order-xl="0">
          <v-row v-if="value.title || value.paragraph" class="pb-6">
            <v-col>
              <TitleParagraphBlock
                em-class-title="text-decoration-underline"
                :value="value"
              />
            </v-col>
          </v-row>

          <v-row v-if="value.items && value.items.length">
            <v-col
              v-for="item in value.items"
              :key="item.id"
              :class="item.content?.list?.length ? '' : 'align-center'"
              class="d-flex"
              cols="12"
              sm="6"
            >
              <v-avatar
                v-if="typeof item.icon === 'string'"
                class="ma-2"
                size="70"
                style="border-width: 2px"
                rounded="0"
                variant="outlined"
              >
                <v-icon
                  :icon="`hdl:${item.icon}`"
                  color="light-blue-darken-2"
                  size="44"
                />
              </v-avatar>

              <ContentBlock
                :value="item.content"
                class="text-body-0 ml-4"
                em-class="font-weight-black"
              />
            </v-col>
          </v-row>

          <v-row v-if="value.cta && value.cta.title && value.cta.href">
            <v-col>
              <v-btn
                :append-icon="mdiArrowRight"
                :href="value.cta.href"
                class="text-none"
                exact
                size="large"
                variant="text"
              >
                <div class="text-wrap">
                  <StyledText
                    em-class="text-light-blue-darken-2 font-weight-black mx-1"
                    :value="value.cta.title"
                  />
                </div>
              </v-btn>
            </v-col>
          </v-row>

          <v-row v-if="value.nav_items && value.nav_items.length">
            <v-col class="mb-6 mb-xl-0">
              <v-btn
                v-for="item in value.nav_items"
                :key="item.href"
                :append-icon="hrefIcon(item.href)"
                :href="item.href"
                :target="hrefTarget(item.href)"
                class="text-none ma-2"
                exact
                rounded="pill"
                size="large"
                variant="outlined"
                >{{ item.title }}</v-btn
              >
            </v-col>
          </v-row>
        </v-col>

        <v-col
          v-if="value.video"
          class="pb-6 pb-xl-0"
          cols="12"
          xl="6"
          order="1"
          order-xl="0"
        >
          <v-card class="fill-height" rounded="0" variant="flat">
            <VideoCover
              :aspect-ratio="videoAspectRatio"
              :gradient="SERVICE_DETAIL_VIDEO_GRADIENT"
              :value="value.video"
              content-class="d-flex align-end justify-center"
              height="100%"
            >
              <PlayVideo :title="value.video.title" :video="value.video" />
            </VideoCover>
          </v-card>
        </v-col>
        <v-col
          v-else-if="imgResp.src"
          class="pb-6 pb-xl-0"
          cols="12"
          xl="6"
          order="1"
          order-xl="0"
        >
          <v-img
            :aspect-ratio="aspectRatio"
            :sizes="imgResp.sizes"
            :src="imgResp.src"
            :srcset="imgResp.srcset"
            alt="Photo for service details"
            cover
            height="100%"
          />
        </v-col>

        <SideTextCol :opacity="0.4" :value="value.side_text" flip />
      </v-row>
    </v-container>
  </v-sheet>
</template>

<script setup>
import { toRef } from 'vue'
import { SERVICE_DETAIL_VIDEO_GRADIENT } from '#root/lib/consts'
import { useImgResponsive } from '#root/lib/img'
import { hrefIcon, hrefTarget } from '#root/lib/utils'
import { mdiArrowRight } from '@mdi/js'

const props = defineProps({
  color: {
    default: 'surface-light',
    type: String
  },
  value: {
    required: true,
    type: Object
  }
})

const aspectRatio = 16 / 9
const videoAspectRatio = 16 / 9
const imgResp = useImgResponsive(
  toRef(() => ({
    aspectRatio,
    image: props.value.image
  }))
)
</script>
